// Carrying a saved-card charge across the 3DS round trip.
//
// A saved-card recharge is charged SERVER-SIDE (payment-service →
// /v1/payments/create/json), so no browser SDK is in play and 3DS can only be
// done by navigating the whole tab to Razorpay's OTP page. Razorpay returns the
// customer by auto-submitting a FORM — a browser POST whose body a static SPA
// route cannot read. So the outcome has to be recovered another way: stash the
// payment id before leaving, then ask Razorpay for its status on return.
//
// sessionStorage, not localStorage: scoped to the one tab that started the
// payment, so a second tab can never pick up someone else's pending charge, and
// it dies with the tab instead of lingering. Both ends of the trip are the same
// origin in every deployed environment, so the stash survives.

const STORAGE_KEY = 'razorpay.pendingPayment'

// A stash older than this is treated as absent. 3DS is a minutes-long
// interaction, never an hours-long one, so anything older is debris from an
// abandoned attempt — resolving it would report a stale outcome onto whatever
// the customer is doing now.
export const PENDING_PAYMENT_TTL_MS = 15 * 60 * 1000

export interface PendingRazorpayPayment {
  paymentId: string
  orderId: string
  accountId: number
  amount: number
  startedAt: number
}

// Storage is injectable so tests never touch a real Storage, and so a caller in
// a context without sessionStorage can pass its own. Reads go through a getter
// rather than a captured reference because sessionStorage ACCESS itself throws
// in some privacy modes, not just its methods.
const defaultStorage = (): Storage | undefined => {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage
  } catch {
    // Safari private mode / storage disabled by policy.
    return undefined
  }
}

export const savePendingRazorpayPayment = (
  payment: PendingRazorpayPayment,
  storage: Storage | undefined = defaultStorage(),
): void => {
  // A payment we cannot identify on return is worse than none: it would resolve
  // against the wrong id. Refuse to write a half-formed record.
  if (!payment.paymentId || !payment.accountId) return
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(payment))
  } catch {
    // Quota or disabled storage. The return path falls back to its
    // outcome-neutral branch, which is exactly the pre-existing behaviour.
  }
}

export const clearPendingRazorpayPayment = (
  storage: Storage | undefined = defaultStorage(),
): void => {
  try {
    storage?.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do — a stale entry is bounded by PENDING_PAYMENT_TTL_MS anyway.
  }
}

// Returns the stash only when it is COMPLETE, FRESH and belongs to the account
// currently resolved on the return route. Anything else reads as absent, which
// routes the caller to its neutral branch rather than to a wrong answer.
//
// `now` is a parameter, not Date.now(), so the TTL boundary is testable.
export const readPendingRazorpayPayment = (
  accountId: number,
  now: number,
  storage: Storage | undefined = defaultStorage(),
): PendingRazorpayPayment | undefined => {
  let raw: string | null | undefined
  try {
    raw = storage?.getItem(STORAGE_KEY)
  } catch {
    return undefined
  }
  if (!raw) return undefined

  let parsed: Partial<PendingRazorpayPayment>
  try {
    parsed = JSON.parse(raw) as Partial<PendingRazorpayPayment>
  } catch {
    // Corrupt entry — never throw on the return path; a customer mid-payment
    // must still land on a working page.
    return undefined
  }

  const { paymentId, orderId, accountId: storedAccountId, amount, startedAt } = parsed
  if (typeof paymentId !== 'string' || !paymentId) return undefined
  if (typeof storedAccountId !== 'number' || storedAccountId !== accountId) return undefined
  if (typeof startedAt !== 'number' || now - startedAt > PENDING_PAYMENT_TTL_MS) return undefined
  // A clock that jumped backwards (or a forged entry) must not extend the TTL.
  if (startedAt > now) return undefined

  return {
    paymentId,
    orderId: typeof orderId === 'string' ? orderId : '',
    accountId: storedAccountId,
    amount: typeof amount === 'number' ? amount : 0,
    startedAt,
  }
}
